/**
 * RCC Surface #3 — CTR Worksheet.
 *
 * FinCEN CTR required when cash in/out single transaction > $10,000.01 (or
 * aggregate same-day same-owner). This is a worksheet — submission to FinCEN
 * BSA E-Filing remains manual in v3.0.
 */

import { useMemo, useState } from 'react';
import { Button, Pill } from '@/components/primitives';
import { SurfaceHeader } from '../components/SurfaceHeader';

interface CashLine {
  id: string;
  timeOfDay: string;
  direction: 'in' | 'out';
  amount: number;
  instrument: string;
  transactor: string;
}

const emptyLine = (): CashLine => ({
  id: `line-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  timeOfDay: '',
  direction: 'in',
  amount: 0,
  instrument: 'cash',
  transactor: '',
});

const CTR_THRESHOLD = 10_000;
const MONETARY_INSTRUMENT_THRESHOLD = 1_500;
const CASH_IN_NOTATE_THRESHOLD = 5_000;
const CASH_OUT_BENEFICIARY_THRESHOLD = 3_000;

export function CtrWorksheet(): JSX.Element {
  const [beneficiary, setBeneficiary] = useState<string>('');
  const [businessAccount, setBusinessAccount] = useState<boolean>(false);
  const [lines, setLines] = useState<CashLine[]>([emptyLine()]);

  const totals = useMemo(() => {
    const cashIn = lines.filter((l) => l.direction === 'in' && l.instrument === 'cash').reduce((s, l) => s + l.amount, 0);
    const cashOut = lines.filter((l) => l.direction === 'out' && l.instrument === 'cash').reduce((s, l) => s + l.amount, 0);
    const monetary = lines.filter((l) => l.instrument !== 'cash').reduce((s, l) => s + l.amount, 0);
    return { cashIn, cashOut, monetary, aggregate: cashIn + cashOut };
  }, [lines]);

  const ctrRequired = totals.aggregate > CTR_THRESHOLD;
  const noteMonetary = totals.monetary > MONETARY_INSTRUMENT_THRESHOLD;
  const noteCashIn = totals.cashIn > CASH_IN_NOTATE_THRESHOLD;
  const requireBeneficiary = totals.cashOut > CASH_OUT_BENEFICIARY_THRESHOLD;

  const addLine = (): void => setLines((prev) => [...prev, emptyLine()]);
  const removeLine = (id: string): void => setLines((prev) => prev.filter((l) => l.id !== id));
  const updateLine = (id: string, patch: Partial<CashLine>): void =>
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));

  return (
    <div>
      <SurfaceHeader
        title="CTR Worksheet"
        scope="Currency Transaction Report worksheet. FinCEN threshold $10k daily aggregate. Submission remains manual via FinCEN BSA E-Filing portal."
        surfaceNumber={3}
        role="FSR"
        actions={
          ctrRequired ? <Pill variant="err">CTR REQUIRED</Pill> : <Pill variant="muted">Under threshold</Pill>
        }
      />

      <section
        style={{
          background: 'var(--surface-card)',
          border: '1px solid var(--surface-border)',
          borderRadius: 'var(--r-md)',
          padding: 'var(--sp-7)',
          marginBottom: 'var(--sp-6)',
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 'var(--sp-5)', marginBottom: 'var(--sp-5)' }}>
          <div>
            <label
              style={{
                display: 'block',
                fontSize: 11,
                textTransform: 'uppercase',
                color: 'var(--ink-500)',
                marginBottom: 'var(--sp-3)',
              }}
            >
              Beneficiary (account owner / business)
            </label>
            <input
              type="text"
              value={beneficiary}
              onChange={(e) => setBeneficiary(e.target.value)}
              style={{
                width: '100%',
                padding: 'var(--sp-4)',
                border: '1px solid var(--ink-300)',
                borderRadius: 'var(--r-md)',
                fontSize: 13,
              }}
            />
          </div>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-3)', alignSelf: 'end' }}>
            <input
              type="checkbox"
              checked={businessAccount}
              onChange={(e) => setBusinessAccount(e.target.checked)}
            />
            <span style={{ fontSize: 13 }}>Business account (remove auto-pulled owner)</span>
          </label>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--ink-50)', textAlign: 'left' }}>
              <th style={{ padding: 'var(--sp-3)' }}>Time</th>
              <th style={{ padding: 'var(--sp-3)' }}>Direction</th>
              <th style={{ padding: 'var(--sp-3)' }}>Instrument</th>
              <th style={{ padding: 'var(--sp-3)', textAlign: 'right' }}>Amount</th>
              <th style={{ padding: 'var(--sp-3)' }}>Transactor</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line.id} style={{ borderTop: '1px solid var(--ink-200)' }}>
                <td style={{ padding: 'var(--sp-3)' }}>
                  <input
                    type="time"
                    value={line.timeOfDay}
                    onChange={(e) => updateLine(line.id, { timeOfDay: e.target.value })}
                    style={{ padding: 'var(--sp-2)', border: '1px solid var(--ink-300)', borderRadius: 'var(--r-sm)' }}
                  />
                </td>
                <td style={{ padding: 'var(--sp-3)' }}>
                  <select
                    value={line.direction}
                    onChange={(e) => updateLine(line.id, { direction: e.target.value as 'in' | 'out' })}
                    style={{ padding: 'var(--sp-2)', border: '1px solid var(--ink-300)', borderRadius: 'var(--r-sm)' }}
                  >
                    <option value="in">Cash in</option>
                    <option value="out">Cash out</option>
                  </select>
                </td>
                <td style={{ padding: 'var(--sp-3)' }}>
                  <select
                    value={line.instrument}
                    onChange={(e) => updateLine(line.id, { instrument: e.target.value })}
                    style={{ padding: 'var(--sp-2)', border: '1px solid var(--ink-300)', borderRadius: 'var(--r-sm)' }}
                  >
                    <option value="cash">Cash</option>
                    <option value="cashiers-check">Cashier's check</option>
                    <option value="money-order">Money order</option>
                    <option value="travelers-check">Traveler's check</option>
                  </select>
                </td>
                <td style={{ padding: 'var(--sp-3)', textAlign: 'right' }}>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={line.amount}
                    onChange={(e) => updateLine(line.id, { amount: Number(e.target.value) || 0 })}
                    style={{
                      width: 120,
                      padding: 'var(--sp-2)',
                      border: '1px solid var(--ink-300)',
                      borderRadius: 'var(--r-sm)',
                      textAlign: 'right',
                    }}
                  />
                </td>
                <td style={{ padding: 'var(--sp-3)' }}>
                  <input
                    type="text"
                    value={line.transactor}
                    onChange={(e) => updateLine(line.id, { transactor: e.target.value })}
                    style={{ padding: 'var(--sp-2)', border: '1px solid var(--ink-300)', borderRadius: 'var(--r-sm)' }}
                  />
                </td>
                <td style={{ padding: 'var(--sp-3)' }}>
                  <Button variant="link" size="sm" onClick={() => removeLine(line.id)}>
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Button variant="ghost" size="sm" onClick={addLine} style={{ marginTop: 'var(--sp-4)' }}>
          Add line
        </Button>
      </section>

      <section
        style={{
          background: 'var(--surface-card)',
          border: '1px solid var(--surface-border)',
          borderRadius: 'var(--r-md)',
          padding: 'var(--sp-7)',
        }}
      >
        <h3 style={{ marginTop: 0 }}>Totals + rule evaluation</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--sp-5)' }}>
          <Stat label="Cash IN" value={totals.cashIn} />
          <Stat label="Cash OUT" value={totals.cashOut} />
          <Stat label="Monetary instruments" value={totals.monetary} />
          <Stat label="Aggregate" value={totals.aggregate} highlight={ctrRequired} />
        </div>
        <ul style={{ marginTop: 'var(--sp-5)', padding: 0, listStyle: 'none', fontSize: 12, display: 'grid', gap: 'var(--sp-3)' }}>
          <RuleRow active={ctrRequired} text="> $10,000 aggregate daily — CTR required." />
          <RuleRow active={noteMonetary} text="> $1,500 monetary instrument — require transactor." />
          <RuleRow active={noteCashIn} text="> $5,000 cash in — notate instrument line." />
          <RuleRow active={requireBeneficiary} text="> $3,000 cash out — require beneficiary + transactor." />
        </ul>
      </section>
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: number; highlight?: boolean }): JSX.Element {
  return (
    <div
      style={{
        padding: 'var(--sp-5)',
        background: highlight ? 'var(--err-bg)' : 'var(--ink-50)',
        color: highlight ? 'var(--err-text)' : 'var(--ink-900)',
        borderRadius: 'var(--r-md)',
      }}
    >
      <div style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--ink-600)' }}>{label}</div>
      <div style={{ fontSize: 20, fontFamily: 'var(--font-mono)', fontWeight: 700, marginTop: 'var(--sp-2)' }}>
        ${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </div>
    </div>
  );
}

function RuleRow({ active, text }: { active: boolean; text: string }): JSX.Element {
  return (
    <li style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
      <Pill variant={active ? 'warn' : 'muted'}>{active ? 'Triggered' : 'OK'}</Pill>
      <span style={{ color: active ? 'var(--ink-900)' : 'var(--ink-500)' }}>{text}</span>
    </li>
  );
}
